import { useState, useCallback, useEffect } from 'react';
import {
  Employee,
  EmployeePosition,
  EmployeeStats,
  EmployeeFilters,
  CreateEmployeeData,
  UpdateEmployeeData,
  EmployeeStatus,
  Location
} from '../types/employee.types';
import { mockEmployees, mockLocations, mockPositions } from '../mocks/employees.mock';
import { usePositions } from './usePositions';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface UseEmployeesReturn {
  employees: Employee[];
  positions: EmployeePosition[];
  locations: Location[];
  stats: EmployeeStats;
  filters: EmployeeFilters;
  loading: boolean;
  error: string | null;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  selectedEmployee: Employee | null;
  updateFilters: (filters: Partial<EmployeeFilters>) => void;
  createEmployee: (data: CreateEmployeeData) => Promise<void>;
  updateEmployee: (id: string, data: UpdateEmployeeData) => Promise<void>;
  toggleEmployeeStatus: (id: string, status: EmployeeStatus) => Promise<void>;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (employee: Employee) => void;
  closeEditModal: () => void;
  reload: () => void;
}

const calculateStats = (employees: Employee[]): EmployeeStats => {
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter(emp => emp.status === 'active').length;
  const inactiveEmployees = employees.filter(emp => emp.status === 'inactive').length;
  
  return {
    totalEmployees,
    activeEmployees,
    inactiveEmployees,
    currentlyWorking: Math.floor(activeEmployees * 0.7), // Simulated
    attendanceRate: totalEmployees > 0 ? (activeEmployees / totalEmployees) * 100 : 0,
    avgPerformanceScore: 4.2 // Simulated
  };
};

const filterAndSortEmployees = (employees: Employee[], filters: EmployeeFilters): Employee[] => {
  let filtered = employees;

  // Filter by search query
  if (filters.searchQuery?.trim()) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter(emp => 
      emp.firstName.toLowerCase().includes(query) ||
      emp.lastName.toLowerCase().includes(query) ||
      emp.email.toLowerCase().includes(query) ||
      emp.dni.includes(query)
    );
  }

  // Filter by status
  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(emp => emp.status === filters.status);
  }

  // Filter by position
  if (filters.position && filters.position !== 'all') {
    filtered = filtered.filter(emp => emp.position.name === filters.position);
  }

  // Filter by location (keeping existing logic for backward compatibility)
  if (filters.locationFilter && filters.locationFilter !== 'all') {
    filtered = filtered.filter(emp => emp.locationId === filters.locationFilter);
  }

  // Sort
  filtered.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (filters.sortBy) {
      case 'name':
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
        break;
      case 'email':
        aValue = a.email;
        bValue = b.email;
        break;
      case 'position':
        aValue = a.position.name;
        bValue = b.position.name;
        break;
      case 'hireDate':
        aValue = new Date(a.hireDate).getTime();
        bValue = new Date(b.hireDate).getTime();
        break;
      default:
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return (filters.sortOrder || 'asc') === 'desc' ? -comparison : comparison;
    } else {
      const comparison = (aValue as number) - (bValue as number);
      return (filters.sortOrder || 'asc') === 'desc' ? -comparison : comparison;
    }
  });

  return filtered;
};

export const useEmployees = (): UseEmployeesReturn => {
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  // Use dynamic positions from database
  const { positions } = usePositions();
  const [filters, setFilters] = useState<EmployeeFilters>({
    searchQuery: '',
    status: 'all',
    position: 'all',
    locationFilter: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Load data from API
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch employees
      const employeesResponse = await fetch(`${API_BASE_URL}/employees`, { headers });
      if (!employeesResponse.ok) {
        if (employeesResponse.status === 401) {
          // Token expired or invalid, fallback to mock data
          console.warn('Token expired, using mock data');
          setAllEmployees([...mockEmployees]);
          setLocations([...mockLocations]);
          return;
        }
        throw new Error('Error al cargar los empleados');
      }

      const employeesData = await employeesResponse.json();
      if (employeesData.success) {
        setAllEmployees(employeesData.data.employees);

        // For locations, fetch all available locations for assignment (not filtered by user access)
        try {
          const locationsResponse = await fetch(`${API_BASE_URL}/locations`, { headers });
          if (locationsResponse.ok) {
            const locationsData = await locationsResponse.json();
            if (locationsData.success && locationsData.data) {
              // Map locations from database to expected format
              const allLocations = locationsData.data.map((loc: any) => ({
                id: loc.id,
                name: loc.nombre, // Map 'nombre' to 'name'
                address: loc.direccion, // Map 'direccion' to 'address'
                active: loc.estado === 'ACTIVO' // Map 'estado' to 'active'
              }));
              setLocations(allLocations);
            } else {
              setLocations([...mockLocations]);
            }
          } else {
            setLocations([...mockLocations]);
          }
        } catch {
          setLocations([...mockLocations]);
        }
      } else {
        throw new Error(employeesData.error || 'Error al cargar los empleados');
      }
    } catch (err) {
      console.error('Error loading employees:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar los empleados');
      // Fallback to mock data
      setAllEmployees([...mockEmployees]);
      setLocations([...mockLocations]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter and sort employees
  const employees = filterAndSortEmployees(allEmployees, filters);
  const stats = calculateStats(allEmployees);

  const updateFilters = useCallback((newFilters: Partial<EmployeeFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const createEmployee = useCallback(async (data: CreateEmployeeData) => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_BASE_URL}/employees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear empleado');
      }

      const responseData = await response.json();
      if (responseData.success) {
        // Reload data to get the updated list
        await loadData();
        setIsCreateModalOpen(false);
      } else {
        throw new Error(responseData.error || 'Error al crear empleado');
      }
    } catch (err) {
      console.error('Error creating employee:', err);
      // Don't set global error state - let the modal handle it
      // setError(err instanceof Error ? err.message : 'Error al crear empleado');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  const updateEmployee = useCallback(async (id: string, data: UpdateEmployeeData) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar empleado');
      }

      const responseData = await response.json();
      if (responseData.success) {
        // Reload data to get the updated list
        await loadData();
        setIsEditModalOpen(false);
        setSelectedEmployee(null);
      } else {
        throw new Error(responseData.error || 'Error al actualizar empleado');
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      setError(err instanceof Error ? err.message : 'Error al actualizar empleado');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadData]);


  const toggleEmployeeStatus = useCallback(async (id: string, newStatus: EmployeeStatus) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setAllEmployees(prev => prev.map(emp => 
        emp.id === id 
          ? { ...emp, status: newStatus, updatedAt: new Date().toISOString() }
          : emp
      ));
    } catch (err) {
      setError('Error al cambiar estado del empleado');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
    setSelectedEmployee(null);
  }, []);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const openEditModal = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedEmployee(null);
  }, []);

  const reload = useCallback(() => {
    loadData();
  }, [loadData]);

  return {
    employees,
    positions,
    locations,
    stats,
    filters,
    loading,
    error,
    isCreateModalOpen,
    isEditModalOpen,
    selectedEmployee,
    updateFilters,
    createEmployee,
    updateEmployee,
    toggleEmployeeStatus,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    reload
  };
};